/**
 * Authentication Service
 * 
 * Handles all authentication-related API calls.
 */

import { apiService, ApiResponse } from './api.service';
import { API_ENDPOINTS, STORAGE_KEYS } from '../config/api.config';

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'super_admin' | 'admin' | 'manager' | 'staff' | 'viewer' | 'chef';
  avatar?: string;
  phone?: string;
  permissions: string[];
  createdAt: string;
}

export interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  avatar?: string;
  bio?: string;
  location?: string;
}

export interface LoginResponse {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface SessionContextResponse {
  user: any;
  tenant: any;
  branches: any[];
  activeBranch: any | null;
  canSwitchTenants: boolean;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  password: string;
  passwordConfirmation: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

class AuthService {
  /**
   * Login user
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await apiService.post<LoginResponse>(
        API_ENDPOINTS.AUTH.LOGIN,
        credentials
      );

      if (response.success && response.data) {
        // Store tokens and user data
        apiService.setToken(response.data.accessToken);
        apiService.setRefreshToken(response.data.refreshToken);
        this.setUser(response.data.user);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await apiService.post<LoginResponse>(
        API_ENDPOINTS.AUTH.REGISTER,
        data
      );

      if (response.success && response.data) {
        // Store tokens and user data
        apiService.setToken(response.data.accessToken);
        apiService.setRefreshToken(response.data.refreshToken);
        this.setUser(response.data.user);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await apiService.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      // Even if API call fails, clear local auth data
      console.error('Logout error:', error);
    } finally {
      this.clearAuth();
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<ApiResponse<AuthUser>> {
    try {
      return await apiService.get<AuthUser>(API_ENDPOINTS.AUTH.ME);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get current user + effective tenant + branches + active branch in one payload.
   */
  async getSessionContext(): Promise<ApiResponse<SessionContextResponse>> {
    try {
      return await apiService.get<SessionContextResponse>(API_ENDPOINTS.AUTH.CONTEXT);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<ApiResponse<{ accessToken: string; expiresIn: number }>> {
    try {
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.post<{ accessToken: string; expiresIn: number }>(
        API_ENDPOINTS.AUTH.REFRESH_TOKEN,
        { refreshToken }
      );

      if (response.success && response.data) {
        apiService.setToken(response.data.accessToken);
      }

      return response;
    } catch (error) {
      this.clearAuth();
      throw error;
    }
  }

  /**
   * Request password reset
   */
  async forgotPassword(data: ForgotPasswordData): Promise<ApiResponse<{ message: string }>> {
    try {
      return await apiService.post<{ message: string }>(
        API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
        data
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordData): Promise<ApiResponse<{ message: string }>> {
    try {
      return await apiService.post<{ message: string }>(
        API_ENDPOINTS.AUTH.RESET_PASSWORD,
        data
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<ApiResponse<{ message: string }>> {
    try {
      return await apiService.post<{ message: string }>(
        API_ENDPOINTS.AUTH.VERIFY_EMAIL,
        { token }
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Change password for authenticated user
   */
  async changePassword(data: ChangePasswordData): Promise<ApiResponse<{ message: string }>> {
    try {
      return await apiService.post<{ message: string }>(
        API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
        data
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update authenticated user's profile
   */
  async updateProfile(data: UpdateProfileData): Promise<ApiResponse<any>> {
    try {
      return await apiService.patch<any>(
        API_ENDPOINTS.AUTH.UPDATE_PROFILE,
        data
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get stored user data
   */
  getUser(): AuthUser | null {
    const userJson = localStorage.getItem(STORAGE_KEYS.USER);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  /**
   * Set user data in storage
   */
  setUser(user: AuthUser): void {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const user = this.getUser();
    return !!token && !!user;
  }

  /**
   * Clear all authentication data
   */
  clearAuth(): void {
    apiService.clearAuth();
  }
}

// Export singleton instance
export const authService = new AuthService();